import { FC, useCallback, useMemo, memo } from 'react'
import * as styles from 'components/features/pfm/ResourceFollowButton/ResourceFollowButton.styl'
import cn from 'classnames'
import { useStoreSelector, useStoreDispatch, useShallowSelector } from 'helpers/reactReduxHelpers'
import { createFeedTagSelector, createFeedAuthorSelector, feedAppSelector } from 'selectors/feedSelectors'
import { FeedLinkLoggedOut, ReturnUrlResource } from 'components/features/socialFeed/FeedLinkLoggedOut/FeedLinkLoggedOut'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'
import { toggleAuthorFollowingActionCreator, toggleTagFollowingActionCreator } from 'actions/feedActions'
import Button from 'components/features/pfm/Button/Button'
import { AnalyticsAction, FollowState } from 'interfaces/analytics'
import { FeedResource, FeedTag, FeedUser } from 'interfaces/feed'

type ResourceFollowButtonProps =
  & Exclude<FeedResource, { type: 'post' }>
  & {
    mode: 'small' | 'medium' | 'large'
  }

export const ResourceFollowButton: FC<ResourceFollowButtonProps> = memo((props) => {
  const { mode, type } = props

  const feedAuthorSelector = useMemo(createFeedAuthorSelector, [])
  const feedTagSelector = useMemo(createFeedTagSelector, [])

  const user = useStoreSelector<FeedUser | null>((state) => props.type === 'user' ? feedAuthorSelector(state, props) : null)
  const tag = useStoreSelector<FeedTag | null>((state) => props.type === 'tag' ? feedTagSelector(state, props) : null)

  const { authorFollowingLoading, tagFollowingLoading } = useShallowSelector((state) => ({
    authorFollowingLoading: Boolean(user && feedAppSelector(state).authorFollowingLoadingIds[user.uid]),
    tagFollowingLoading: Boolean(tag && feedAppSelector(state).tagFollowingLoadingIds[tag.name]),
  }))

  const dispatch = useStoreDispatch()

  const toggleFollowAction = useMemo(() => {
    if (!user && !tag) {
      return null
    }

    if (type === 'user' && user) {
      return toggleAuthorFollowingActionCreator.request({
        toFollow: !user.is_followed,
        uid: user.uid,
      })
    }

    if (type === 'tag' && tag) {
      return toggleTagFollowingActionCreator.request({
        toFollow: !tag.is_followed,
        tag_name: tag.name,
      })
    }

    return null
  }, [user, tag, type])

  const handleToggleFollowResource = useCallback(() => {
    if (!toggleFollowAction) {
      return
    }

    dispatch(toggleFollowAction)
  }, [dispatch, toggleFollowAction])

  const wrapperClassName = cn(styles.buttonWrapper, {
    [styles[mode]]: true,
  })

  type ResourceAnalyticsAction =
    | AnalyticsAction<'page_controls-feed_author', 'toggle-follow', FollowState>
    | AnalyticsAction<'page_controls-feed_tag', 'toggle-follow', FollowState>

  const analyticsAction = useMemo((): ResourceAnalyticsAction | null => {
    if (tag) {
      return {
        category: 'page_controls-feed_tag',
        action: 'toggle-follow',
        label: tag.is_followed ? 'unfollow' : 'follow',
      }
    }

    if (user) {
      return {
        category: 'page_controls-feed_author',
        action: 'toggle-follow',
        label: user.is_followed ? 'unfollow' : 'follow',
      }
    }

    return null
  }, [tag, user])

  const linkLoggedOutProps = useMemo((): ReturnUrlResource | null => {
    if (user) {
      return {
        type: 'user',
        username: user.username,
      }
    }

    if (tag) {
      return {
        type: 'tag',
        tagName: tag.name,
      }
    }

    return null
  }, [tag, user])

  const isFollowed = user
    ? user.is_followed
    : tag
      ? tag.is_followed
      : false

  if (!user && !tag || !linkLoggedOutProps || !analyticsAction) {
    return null
  }

  return (
    <AnalyticsClick
      className={ wrapperClassName }
      { ...analyticsAction }
    >
      <FeedLinkLoggedOut { ...linkLoggedOutProps }>
        {
          (isAuthenticated) => {
            const handleClick = isAuthenticated ? handleToggleFollowResource : undefined

            return (
              <Button
                fullWidth
                size='tiny'
                style={ isFollowed ? 'outlined' : undefined }
                disabled={ authorFollowingLoading || tagFollowingLoading }
                shadow='light'
                onClick={ handleClick }
              >
                { isFollowed ? 'Unfollow' : 'Follow' }
              </Button>
            )
          }
        }
      </FeedLinkLoggedOut>
    </AnalyticsClick>
  )
})
